/**
 * Best-effort app identity for a URL the byline cannot identify any other way: a third-party Smart
 * Link or a plain hyperlink, which the Confluence backend returns with `product: null`. Keyed on the
 * registrable domain, with path rules where one domain hosts several apps. Unknown hosts return
 * `null` and are not counted as an app (CCINT-9543, DACI Option 4).
 *
 * Hosts were reconciled on 2026-10-26 against three sources: each vendor's own documentation of
 * its object URLs, the ORS Forge object-provider manifests (`atlassian/forge-smart-links`,
 * `platform:objectProvider.domains`), and the Rovo connector service's host allowlist for links
 * shared in Slack (`atlassian/ai-3p-connector`, `slack-shared-link-domain-allowlist.yml`), which
 * matches hosts the same way this file does. The Rovo connector catalogue was the seed; the list
 * then grew to common work SaaS. See the CCINT-9543 work notes for the per-host evidence.
 *
 * A vendor-owned host resolves to the vendor even when it serves marketing or docs
 * (`www.figma.com/pricing` is still Figma): a known name is better than unknown. Domains shared by
 * several apps (`google.com`, `microsoft.com`, `cloud.microsoft`, `adobe.com`, `amazon.com`,
 * `atlassian.com`) list their leaf hosts first and fall back to the vendor itself. The list also
 * names the organisations and reference sites most often pasted into pages (Nov 2026 paste data),
 * so a link to a company's own site counts as that company rather than as nothing.
 *
 * Customer-owned domains (Zendesk help centres, GitBook docs, ServiceNow portals, Notion Sites,
 * Statuspage pages, GitHub Enterprise Server, self-managed GitLab) cannot be matched, and
 * placeholder hosts (`example.com`) are left unknown on purpose.
 */

type UrlAppRule = {
	/** Registrable domain or leaf host, matched as the host itself or any subdomain of it. */
	domain: string;
	/** Picks the app from the parsed URL; `null` leaves the URL unidentified. */
	resolve: (url: URL) => string | null;
};

const constant =
	(appId: string): UrlAppRule['resolve'] =>
	() =>
		appId;

const byPathPrefix =
	(prefixes: ReadonlyArray<readonly [string, string]>, fallback: string | null) =>
	(url: URL): string | null =>
		prefixes.find(([prefix]) => url.pathname.startsWith(prefix))?.[1] ?? fallback;

// One Atlassian site host serves Confluence, Jira and Compass, so the path decides.
const atlassianSite = byPathPrefix(
	[
		['/wiki', 'confluence'],
		['/browse/', 'jira'],
		['/jira/', 'jira'],
		['/servicedesk/', 'jira'],
		['/compass', 'compass'],
	],
	null,
);

// Business OneDrive libraries live on the `<tenant>-my` host of the SharePoint domain.
const sharePointOrOneDrive = (url: URL): string =>
	/-my\.sharepoint\.(com|us|cn)(\.mcas\.ms)?$|-my\.sharepoint-mil\.us$/.test(url.hostname)
		? 'onedrive'
		: 'sharepoint';

// Docs, Sheets, Slides and Forms deliberately collapse onto Google Drive. A viewer who has not
// connected Google sees one generic Drive chip for all of them on the client, and the server cannot
// know the connection state, so it counts the lower number (one app) rather than risk reserving
// tiles the client will fill with fallback icons.
// Object URLs carry an `s3` host label (`bucket.s3.region.amazonaws.com`, `s3.amazonaws.com`,
// `bucket.s3-accesspoint.region.amazonaws.com`); other AWS services on the same domain do not.
const amazonWebServices = (url: URL): string =>
	url.hostname.split('.').some((label) => label === 's3' || label.startsWith('s3-'))
		? 'aws s3'
		: 'aws';

// Rules are checked in order; put the more specific host first when two share a domain.
const URL_APP_RULES: readonly UrlAppRule[] = [
	// Atlassian
	{ domain: 'atlassian.net', resolve: atlassianSite },
	{ domain: 'jira.com', resolve: atlassianSite },
	{ domain: 'atlassian-us-gov-mod.com', resolve: atlassianSite },
	{ domain: 'atlassian-us-gov-mod.net', resolve: atlassianSite },
	{ domain: 'jira.atlassian.cloud', resolve: constant('jira') },
	{ domain: 'home.atlassian.com', resolve: constant('goals') },
	{ domain: 'team.atlassian.com', resolve: constant('goals') },
	{ domain: 'confluence.atlassian.com', resolve: constant('confluence') },
	{ domain: 'jira.atlassian.com', resolve: constant('jira') },
	{ domain: 'status.atlassian.com', resolve: constant('statuspage') },
	{ domain: 'admin.atlassian.com', resolve: constant('atlassian administration') },
	{ domain: 'atlassian.com', resolve: constant('atlassian') },
	{ domain: 'atl-paas.net', resolve: constant('atlassian') },
	{ domain: 'refined.site', resolve: constant('refined') },
	{ domain: 'bitbucket.org', resolve: constant('bitbucket') },
	{ domain: 'loom.com', resolve: constant('loom') },
	{ domain: 'trello.com', resolve: constant('trello') },
	{ domain: 'statuspage.io', resolve: constant('statuspage') },
	{ domain: 'opsgenie.com', resolve: constant('opsgenie') },
	{ domain: 'jiraalign.com', resolve: constant('jira align') },
	{ domain: 'agilecraft.com', resolve: constant('jira align') },

	// Design and whiteboarding
	{ domain: 'figma.com', resolve: constant('figma') },
	{ domain: 'figma-gov.com', resolve: constant('figma') },
	{ domain: 'figma.site', resolve: constant('figma') },
	{ domain: 'miro.com', resolve: constant('miro') },
	{ domain: 'realtimeboard.com', resolve: constant('miro') },
	{ domain: 'canva.com', resolve: constant('canva') },
	{ domain: 'canva.link', resolve: constant('canva') },
	{ domain: 'lucid.app', resolve: constant('lucid') },
	{ domain: 'lucidgov.app', resolve: constant('lucid') },
	{ domain: 'lucidextensions.app', resolve: constant('lucid') },
	{ domain: 'lucid.co', resolve: constant('lucid') },
	{ domain: 'lucidchart.com', resolve: constant('lucid') },
	{ domain: 'lucidspark.com', resolve: constant('lucid') },
	{ domain: 'lucidscale.com', resolve: constant('lucid') },
	{ domain: 'mural.co', resolve: constant('mural') },
	{ domain: 'whimsical.com', resolve: constant('whimsical') },
	{ domain: 'balsamiq.cloud', resolve: constant('balsamiq') },
	{ domain: 'balsamiq.com', resolve: constant('balsamiq') },
	{ domain: 'sketch.com', resolve: constant('sketch') },
	{ domain: 'sketch.cloud', resolve: constant('sketch') },
	{ domain: 'zeplin.io', resolve: constant('zeplin') },
	{ domain: 'zpl.io', resolve: constant('zeplin') },
	{ domain: 'xd.adobe.com', resolve: constant('adobe xd') },
	{ domain: 'framer.com', resolve: constant('framer') },
	{ domain: 'framer.app', resolve: constant('framer') },
	{ domain: 'framer.website', resolve: constant('framer') },
	{ domain: 'framer.photos', resolve: constant('framer') },
	{ domain: 'framer.media', resolve: constant('framer') },
	{ domain: 'framer.wiki', resolve: constant('framer') },
	{ domain: 'webflow.com', resolve: constant('webflow') },
	{ domain: 'webflow.io', resolve: constant('webflow') },

	// Microsoft: leaf hosts first, then the shared app domains.
	{ domain: 'sharepoint.com', resolve: sharePointOrOneDrive },
	{ domain: 'sharepoint.us', resolve: sharePointOrOneDrive },
	{ domain: 'sharepoint-mil.us', resolve: sharePointOrOneDrive },
	{ domain: 'sharepoint.cn', resolve: sharePointOrOneDrive },
	{ domain: 'sharepoint.com.mcas.ms', resolve: sharePointOrOneDrive },
	{ domain: 'onedrive.live.com', resolve: constant('onedrive') },
	{ domain: 'onedrive.com', resolve: constant('onedrive') },
	{ domain: '1drv.ms', resolve: constant('onedrive') },
	{ domain: 'teams.microsoft.com', resolve: constant('microsoft teams') },
	{ domain: 'teams.cloud.microsoft', resolve: constant('microsoft teams') },
	{ domain: 'teams.live.com', resolve: constant('microsoft teams') },
	{ domain: 'gov.teams.microsoft.us', resolve: constant('microsoft teams') },
	{ domain: 'dod.teams.microsoft.us', resolve: constant('microsoft teams') },
	{ domain: 'outlook.office.com', resolve: constant('outlook') },
	{ domain: 'outlook.office365.com', resolve: constant('outlook') },
	{ domain: 'outlook.cloud.microsoft', resolve: constant('outlook') },
	{ domain: 'outlook.live.com', resolve: constant('outlook') },
	{ domain: 'powerbi.com', resolve: constant('microsoft power bi') },
	{ domain: 'powerbigov.us', resolve: constant('microsoft power bi') },
	{ domain: 'powerbi.cn', resolve: constant('microsoft power bi') },
	{ domain: 'dev.azure.com', resolve: constant('azure devops') },
	{ domain: 'visualstudio.com', resolve: constant('azure devops') },
	{ domain: 'onenote.com', resolve: constant('onenote') },
	{ domain: 'onenote.cloud.microsoft', resolve: constant('onenote') },
	{ domain: 'planner.cloud.microsoft', resolve: constant('microsoft planner') },
	{ domain: 'tasks.office.com', resolve: constant('microsoft planner') },
	{ domain: 'loop.cloud.microsoft', resolve: constant('microsoft loop') },
	{ domain: 'loop.microsoft.com', resolve: constant('microsoft loop') },
	{ domain: 'whiteboard.microsoft.com', resolve: constant('microsoft whiteboard') },
	{ domain: 'whiteboard.cloud.microsoft', resolve: constant('microsoft whiteboard') },
	{ domain: 'whiteboard.ms', resolve: constant('microsoft whiteboard') },
	{ domain: 'wbd.ms', resolve: constant('microsoft whiteboard') },
	{ domain: 'forms.office.com', resolve: constant('microsoft forms') },
	{ domain: 'forms.microsoft.com', resolve: constant('microsoft forms') },
	{ domain: 'forms.cloud.microsoft', resolve: constant('microsoft forms') },
	{ domain: 'lists.microsoft.com', resolve: constant('microsoft lists') },
	{ domain: 'lists.cloud.microsoft', resolve: constant('microsoft lists') },
	{ domain: 'visio.cloud.microsoft', resolve: constant('visio') },
	{ domain: 'visio.office.com', resolve: constant('visio') },
	{ domain: 'stream.office.com', resolve: constant('microsoft stream') },
	{ domain: 'engage.cloud.microsoft', resolve: constant('viva engage') },
	{ domain: 'yammer.com', resolve: constant('viva engage') },
	{ domain: 'copilot.microsoft.com', resolve: constant('microsoft copilot') },
	{ domain: 'copilot.cloud.microsoft', resolve: constant('microsoft copilot') },
	{
		domain: 'm365.cloud.microsoft',
		resolve: byPathPrefix([['/chat', 'microsoft copilot']], 'microsoft 365'),
	},
	{ domain: 'office.com', resolve: constant('microsoft 365') },
	{ domain: 'microsoft365.com', resolve: constant('microsoft 365') },
	// Any other Microsoft 365 app on the new shared domain is at least Microsoft 365.
	{ domain: 'cloud.microsoft', resolve: constant('microsoft 365') },
	{ domain: 'dynamics.com', resolve: constant('dynamics 365') },
	{ domain: 'powerapps.com', resolve: constant('power apps') },
	{ domain: 'powerautomate.com', resolve: constant('power automate') },
	{ domain: 'flow.microsoft.com', resolve: constant('power automate') },
	{ domain: 'intune.microsoft.com', resolve: constant('microsoft intune') },
	{ domain: 'intune.microsoft.us', resolve: constant('microsoft intune') },
	{ domain: 'endpoint.microsoft.com', resolve: constant('microsoft intune') },
	{ domain: 'entra.microsoft.com', resolve: constant('microsoft entra') },
	{ domain: 'entra.microsoft.us', resolve: constant('microsoft entra') },
	{ domain: 'aad.portal.azure.com', resolve: constant('microsoft entra') },
	{ domain: 'portal.azure.com', resolve: constant('azure') },
	{ domain: 'portal.azure.us', resolve: constant('azure') },
	{ domain: 'portal.azure.cn', resolve: constant('azure') },
	{ domain: 'azurewebsites.net', resolve: constant('azure') },
	{ domain: 'windows.net', resolve: constant('azure') },
	{ domain: 'azure.net', resolve: constant('azure') },
	{ domain: 'azurecr.io', resolve: constant('azure') },
	{ domain: 'azure-api.net', resolve: constant('azure') },
	{ domain: 'security.microsoft.com', resolve: constant('microsoft defender') },
	{ domain: 'view.officeapps.live.com', resolve: constant('microsoft 365') },
	{ domain: 'login.microsoftonline.com', resolve: constant('microsoft 365') },
	{ domain: 'onmicrosoft.com', resolve: constant('microsoft 365') },
	{ domain: 'learn.microsoft.com', resolve: constant('microsoft') },
	{ domain: 'support.microsoft.com', resolve: constant('microsoft') },
	{ domain: 'aka.ms', resolve: constant('microsoft') },
	{ domain: 'asp.net', resolve: constant('microsoft') },
	{ domain: 'microsoft.com', resolve: constant('microsoft') },

	// Google: leaf hosts first; `google.com` itself is shared with search and stays unidentified.
	{ domain: 'docs.google.com', resolve: constant('google drive') },
	{ domain: 'sheets.google.com', resolve: constant('google drive') },
	{ domain: 'slides.google.com', resolve: constant('google drive') },
	{ domain: 'forms.gle', resolve: constant('google drive') },
	{ domain: 'drive.google.com', resolve: constant('google drive') },
	{ domain: 'script.google.com', resolve: constant('google apps script') },
	{ domain: 'calendar.google.com', resolve: constant('google calendar') },
	{ domain: 'mail.google.com', resolve: byPathPrefix([['/chat', 'google chat']], 'gmail') },
	{ domain: 'chat.google.com', resolve: constant('google chat') },
	{ domain: 'meet.google.com', resolve: constant('google meet') },
	{ domain: 'keep.google.com', resolve: constant('google keep') },
	{ domain: 'sites.google.com', resolve: constant('google sites') },
	{ domain: 'groups.google.com', resolve: constant('google groups') },
	{ domain: 'analytics.google.com', resolve: constant('google analytics') },
	{ domain: 'lookerstudio.google.com', resolve: constant('looker studio') },
	{ domain: 'datastudio.google.com', resolve: constant('looker studio') },
	{ domain: 'looker.com', resolve: constant('looker') },
	{ domain: 'looker.app', resolve: constant('looker') },
	{ domain: 'gemini.google.com', resolve: constant('gemini') },
	{ domain: 'g.co', resolve: byPathPrefix([['/gemini', 'gemini']], 'google') },
	{ domain: 'notebooklm.google.com', resolve: constant('notebooklm') },
	{ domain: 'notebook.google.com', resolve: constant('notebooklm') },
	{ domain: 'colab.research.google.com', resolve: constant('google colab') },
	{ domain: 'console.cloud.google.com', resolve: constant('google cloud') },
	{ domain: 'cloud.google.com', resolve: constant('google cloud') },
	{ domain: 'googleapis.com', resolve: constant('google cloud') },
	{ domain: 'googleusercontent.com', resolve: constant('google cloud') },
	{ domain: 'cloudlogging.app.goo.gl', resolve: constant('google cloud') },
	{ domain: 'firebase.google.com', resolve: constant('firebase') },
	{ domain: 'tel.meet', resolve: constant('google meet') },
	{ domain: 'gmail.com', resolve: constant('gmail') },
	{ domain: 'admanager.google.com', resolve: constant('google ad manager') },
	{ domain: 'play.google.com', resolve: constant('google play') },
	{ domain: 'maps.app.goo.gl', resolve: constant('google maps') },
	{ domain: 'maps.google.com', resolve: constant('google maps') },
	{ domain: 'support.google.com', resolve: constant('google') },
	{ domain: 'developers.google.com', resolve: constant('google') },
	{ domain: 'google.com', resolve: byPathPrefix([['/calendar', 'google calendar']], 'google') },

	// Chat and meetings
	{ domain: 'slack.com', resolve: constant('slack') },
	{ domain: 'slack-files.com', resolve: constant('slack') },
	{ domain: 'slack-gov.com', resolve: constant('slack') },
	{ domain: 'zoom.us', resolve: constant('zoom') },
	{ domain: 'zoom.com', resolve: constant('zoom') },
	{ domain: 'zoomgov.com', resolve: constant('zoom') },
	{ domain: 'zoomapp.cloud', resolve: constant('zoom') },
	{ domain: 'webex.com', resolve: constant('webex') },
	{ domain: 'discord.com', resolve: constant('discord') },
	{ domain: 'discord.gg', resolve: constant('discord') },
	{ domain: 'discordapp.com', resolve: constant('discord') },
	{ domain: 'calendly.com', resolve: constant('calendly') },
	{ domain: 'doodle.com', resolve: constant('doodle') },
	{ domain: 'mentimeter.com', resolve: constant('mentimeter') },
	{ domain: 'menti.com', resolve: constant('mentimeter') },
	{ domain: 'slido.com', resolve: constant('slido') },
	{ domain: 'sli.do', resolve: constant('slido') },

	// Video and social
	{ domain: 'vimeo.com', resolve: constant('vimeo') },
	{ domain: 'youtube.com', resolve: constant('youtube') },
	{ domain: 'youtu.be', resolve: constant('youtube') },
	{ domain: 'youtube-nocookie.com', resolve: constant('youtube') },
	{ domain: 'linkedin.com', resolve: constant('linkedin') },
	{ domain: 'lnkd.in', resolve: constant('linkedin') },
	{ domain: 'x.com', resolve: constant('x') },
	{ domain: 'twitter.com', resolve: constant('x') },
	{ domain: 't.co', resolve: constant('x') },

	// Source control, CI and developer platforms
	{ domain: 'github.com', resolve: constant('github') },
	{ domain: 'ghe.com', resolve: constant('github') },
	{ domain: 'githubusercontent.com', resolve: constant('github') },
	{ domain: 'github.io', resolve: constant('github') },
	{ domain: 'gitlab.com', resolve: constant('gitlab') },
	{ domain: 'gitlab-dedicated.com', resolve: constant('gitlab') },
	{ domain: 'gitlab.io', resolve: constant('gitlab') },
	{ domain: 'circleci.com', resolve: constant('circleci') },
	{ domain: 'buildkite.com', resolve: constant('buildkite') },
	{ domain: 'travis-ci.com', resolve: constant('travis ci') },
	{ domain: 'vercel.com', resolve: constant('vercel') },
	{ domain: 'vercel.app', resolve: constant('vercel') },
	{ domain: 'netlify.com', resolve: constant('netlify') },
	{ domain: 'netlify.app', resolve: constant('netlify') },
	{ domain: 'heroku.com', resolve: constant('heroku') },
	{ domain: 'herokuapp.com', resolve: constant('heroku') },
	{ domain: 'postman.co', resolve: constant('postman') },
	{ domain: 'postman.com', resolve: constant('postman') },
	{ domain: 'pstmn.io', resolve: constant('postman') },
	{ domain: 'getpostman.com', resolve: constant('postman') },
	{ domain: 'docker.com', resolve: constant('docker') },
	{ domain: 'npmjs.com', resolve: constant('npm') },
	{ domain: 'jfrog.io', resolve: constant('jfrog') },
	{ domain: 'jfrog.com', resolve: constant('jfrog') },
	{ domain: 'huggingface.co', resolve: constant('hugging face') },
	{ domain: 'hf.co', resolve: constant('hugging face') },
	{ domain: 'replit.com', resolve: constant('replit') },
	{ domain: 'replit.app', resolve: constant('replit') },
	{ domain: 'repl.co', resolve: constant('replit') },
	{ domain: 'lovable.dev', resolve: constant('lovable') },
	{ domain: 'lovable.app', resolve: constant('lovable') },
	{ domain: 'linear.app', resolve: constant('linear') },
	{ domain: 'shortcut.com', resolve: constant('shortcut') },
	{ domain: 'clubhouse.io', resolve: constant('shortcut') },
	{ domain: 'youtrack.cloud', resolve: constant('youtrack') },
	{ domain: 'myjetbrains.com', resolve: constant('youtrack') },
	{ domain: 'stackoverflowteams.com', resolve: constant('stack overflow') },
	{ domain: 'stackenterprise.co', resolve: constant('stack overflow') },
	{ domain: 'stackoverflow.com', resolve: constant('stack overflow') },
	{ domain: 'cursor.com', resolve: constant('cursor') },

	// Observability, security and identity
	{ domain: 'sentry.io', resolve: constant('sentry') },
	{ domain: 'pagerduty.com', resolve: constant('pagerduty') },
	{ domain: 'datadoghq.com', resolve: constant('datadog') },
	{ domain: 'datadoghq.eu', resolve: constant('datadog') },
	{ domain: 'ddog-gov.com', resolve: constant('datadog') },
	{ domain: 'dynatrace.com', resolve: constant('dynatrace') },
	{ domain: 'newrelic.com', resolve: constant('new relic') },
	{ domain: 'onenr.io', resolve: constant('new relic') },
	{ domain: 'observability.splunkcloud.com', resolve: constant('splunk observability') },
	{ domain: 'signalfx.com', resolve: constant('splunk observability') },
	{ domain: 'splunkcloud.com', resolve: constant('splunk') },
	{ domain: 'splunk.com', resolve: constant('splunk') },
	{ domain: 'grafana.net', resolve: constant('grafana') },
	{ domain: 'grafana.com', resolve: constant('grafana') },
	{ domain: 'honeycomb.io', resolve: constant('honeycomb') },
	{ domain: 'rollbar.com', resolve: constant('rollbar') },
	{ domain: 'bugsnag.com', resolve: constant('bugsnag') },
	{ domain: 'bugsnag.smartbear.com', resolve: constant('bugsnag') },
	{ domain: 'snyk.io', resolve: constant('snyk') },
	{ domain: 'snykgov.io', resolve: constant('snyk') },
	{ domain: 'sonarcloud.io', resolve: constant('sonarqube cloud') },
	{ domain: 'sonarqube.us', resolve: constant('sonarqube cloud') },
	{ domain: 'launchdarkly.com', resolve: constant('launchdarkly') },
	{ domain: 'launchdarkly.us', resolve: constant('launchdarkly') },
	{ domain: 'statsig.com', resolve: constant('statsig') },
	{ domain: 'optimizely.com', resolve: constant('optimizely') },
	{ domain: 'lansweeper.com', resolve: constant('lansweeper') },
	{ domain: 'okta.com', resolve: constant('okta') },
	{ domain: 'okta-emea.com', resolve: constant('okta') },
	{ domain: 'oktapreview.com', resolve: constant('okta') },
	{ domain: 'okta-gov.com', resolve: constant('okta') },
	{ domain: 'okta.mil', resolve: constant('okta') },
	{ domain: 'auth0.com', resolve: constant('auth0') },
	{ domain: '1password.com', resolve: constant('1password') },
	{ domain: '1password.ca', resolve: constant('1password') },
	{ domain: '1password.eu', resolve: constant('1password') },
	{ domain: 'cloudflare.com', resolve: constant('cloudflare') },

	// Cloud and data
	{ domain: 'console.aws.amazon.com', resolve: byPathPrefix([['/s3', 'aws s3']], 'aws') },
	{ domain: 'console.amazonaws-us-gov.com', resolve: constant('aws') },
	{ domain: 'console.amazonaws.cn', resolve: constant('aws') },
	{ domain: 'docs.aws.amazon.com', resolve: constant('aws') },
	{ domain: 'aws.amazon.com', resolve: constant('aws') },
	{ domain: 'awsapps.com', resolve: constant('aws') },
	{ domain: 'amplifyapp.com', resolve: constant('aws') },
	{ domain: 'cloudfront.net', resolve: constant('aws') },
	{ domain: 'amazon.com', resolve: constant('amazon') },
	{ domain: 'amazon.de', resolve: constant('amazon') },
	{ domain: 'amazonaws.com', resolve: amazonWebServices },
	{ domain: 'amazonaws.com.cn', resolve: amazonWebServices },
	{ domain: 'databricks.com', resolve: constant('databricks') },
	{ domain: 'azuredatabricks.net', resolve: constant('databricks') },
	{ domain: 'databricks.azure.us', resolve: constant('databricks') },
	{ domain: 'databricks.azure.cn', resolve: constant('databricks') },
	{ domain: 'snowflake.com', resolve: constant('snowflake') },
	{ domain: 'snowflakecomputing.com', resolve: constant('snowflake') },
	{ domain: 'tableau.com', resolve: constant('tableau') },
	{ domain: 'amplitude.com', resolve: constant('amplitude') },
	{ domain: 'mixpanel.com', resolve: constant('mixpanel') },
	{ domain: 'segment.com', resolve: constant('segment') },
	{ domain: 'pendo.io', resolve: constant('pendo') },
	{ domain: 'fullstory.com', resolve: constant('fullstory') },
	{ domain: 'hotjar.com', resolve: constant('hotjar') },

	// Sales, support and finance
	{ domain: 'salesforce.com', resolve: constant('salesforce') },
	{ domain: 'force.com', resolve: constant('salesforce') },
	{ domain: 'salesforce-setup.com', resolve: constant('salesforce') },
	{ domain: 'salesforce-sites.com', resolve: constant('salesforce') },
	{ domain: 'salesforce.app', resolve: constant('salesforce') },
	{ domain: 'my.site.com', resolve: constant('salesforce') },
	{ domain: 'visualforce.com', resolve: constant('salesforce') },
	{ domain: 'cloudforce.com', resolve: constant('salesforce') },
	{ domain: 'demandware.net', resolve: constant('salesforce') },
	{ domain: 'hubspot.com', resolve: constant('hubspot') },
	{ domain: 'pipedrive.com', resolve: constant('pipedrive') },
	{ domain: 'gong.io', resolve: constant('gong') },
	{ domain: 'zendesk.com', resolve: constant('zendesk') },
	{ domain: 'service-now.com', resolve: constant('servicenow') },
	{ domain: 'servicenowservices.com', resolve: constant('servicenow') },
	{ domain: 'servicenow.com', resolve: constant('servicenow') },
	{ domain: 'freshservice.com', resolve: constant('freshservice') },
	{ domain: 'freshdesk.com', resolve: constant('freshdesk') },
	{ domain: 'intercom.com', resolve: constant('intercom') },
	{ domain: 'intercom.io', resolve: constant('intercom') },
	{ domain: 'app.helpdesk.com', resolve: constant('helpdesk') },
	{ domain: 'zoho.eu', resolve: constant('zoho') },
	{ domain: 'zoho.com', resolve: constant('zoho') },
	{ domain: 'frontapp.com', resolve: constant('front') },
	{ domain: 'front.com', resolve: constant('front') },
	{ domain: 'helpscout.net', resolve: constant('help scout') },
	{ domain: 'helpscout.com', resolve: constant('help scout') },
	{ domain: 'stripe.com', resolve: constant('stripe') },
	{ domain: 'docusign.net', resolve: constant('docusign') },
	{ domain: 'docusign.com', resolve: constant('docusign') },
	{ domain: 'adobesign.com', resolve: constant('adobe sign') },
	{ domain: 'adobesign.us', resolve: constant('adobe sign') },
	{ domain: 'echosign.com', resolve: constant('adobe sign') },
	{ domain: 'documents.adobe.com', resolve: constant('adobe sign') },
	{ domain: 'pandadoc.com', resolve: constant('pandadoc') },
	{ domain: 'pandadoc.eu', resolve: constant('pandadoc') },
	{ domain: 'ironcladapp.com', resolve: constant('ironclad') },
	{ domain: 'qbo.intuit.com', resolve: constant('quickbooks') },
	{ domain: 'intuit.com', resolve: constant('intuit') },
	{ domain: 'xero.com', resolve: constant('xero') },
	{ domain: 'netsuite.com', resolve: constant('netsuite') },
	{ domain: 'expensify.com', resolve: constant('expensify') },
	{ domain: 'concursolutions.com', resolve: constant('sap concur') },
	{ domain: 'concur.com', resolve: constant('sap concur') },
	{ domain: 'navan.com', resolve: constant('navan') },
	{ domain: 'tripactions.com', resolve: constant('navan') },
	{ domain: 'brex.com', resolve: constant('brex') },
	{ domain: 'ramp.com', resolve: constant('ramp') },

	// Work management and knowledge
	{ domain: 'asana.com', resolve: constant('asana') },
	{ domain: 'asana-gov.com', resolve: constant('asana') },
	{ domain: 'monday.com', resolve: constant('monday') },
	{ domain: 'smartsheet.com', resolve: constant('smartsheet') },
	{ domain: 'smartsheet.eu', resolve: constant('smartsheet') },
	{ domain: 'smartsheet.au', resolve: constant('smartsheet') },
	{ domain: 'smartsheetgov.com', resolve: constant('smartsheet') },
	{ domain: 'smartsheet.gov', resolve: constant('smartsheet') },
	{ domain: 'clickup.com', resolve: constant('clickup') },
	{ domain: 'todoist.com', resolve: constant('todoist') },
	{ domain: 'wrike.com', resolve: constant('wrike') },
	{ domain: 'basecamp.com', resolve: constant('basecamp') },
	{ domain: 'teamwork.com', resolve: constant('teamwork') },
	{ domain: 'workfront.com', resolve: constant('adobe workfront') },
	{ domain: 'workfront.adobe.com', resolve: constant('adobe workfront') },
	{
		domain: 'experience.adobe.com',
		resolve: byPathPrefix([['/workfront', 'adobe workfront']], 'adobe'),
	},
	{ domain: 'aha.io', resolve: constant('aha') },
	{ domain: 'aha.site', resolve: constant('aha') },
	{ domain: 'productboard.com', resolve: constant('productboard') },
	{ domain: 'airtable.com', resolve: constant('airtable') },
	{ domain: 'notion.so', resolve: constant('notion') },
	{ domain: 'notion.com', resolve: constant('notion') },
	{ domain: 'notion.site', resolve: constant('notion') },
	{ domain: 'coda.io', resolve: constant('coda') },
	{ domain: 'quip.com', resolve: constant('quip') },
	{ domain: 'onquip.com', resolve: constant('quip') },
	{ domain: 'craft.do', resolve: constant('craft') },
	{ domain: 'craft.me', resolve: constant('craft') },
	{ domain: 'evernote.com', resolve: constant('evernote') },
	{ domain: 'slab.com', resolve: constant('slab') },
	{ domain: 'nuclino.com', resolve: constant('nuclino') },
	{ domain: 'dovetail.com', resolve: constant('dovetail') },
	{ domain: 'dovetailapp.com', resolve: constant('dovetail') },
	{ domain: 'getguru.com', resolve: constant('guru') },
	{ domain: 'helpjuice.com', resolve: constant('helpjuice') },
	{ domain: 'gitbook.io', resolve: constant('gitbook') },
	{ domain: 'gitbook.com', resolve: constant('gitbook') },
	{ domain: 'typeform.com', resolve: constant('typeform') },
	{ domain: 'typeform.eu', resolve: constant('typeform') },
	{ domain: 'surveymonkey.com', resolve: constant('surveymonkey') },
	{ domain: 'research.net', resolve: constant('surveymonkey') },
	{ domain: 'qualtrics.com', resolve: constant('qualtrics') },

	// People and HR
	{ domain: 'myworkday.com', resolve: constant('workday') },
	{ domain: 'myworkdayjobs.com', resolve: constant('workday') },
	{ domain: 'myworkdaysite.com', resolve: constant('workday') },
	{ domain: 'workday.com', resolve: constant('workday') },
	{ domain: 'bamboohr.com', resolve: constant('bamboohr') },
	{ domain: 'greenhouse.io', resolve: constant('greenhouse') },
	{ domain: 'lever.co', resolve: constant('lever') },
	{ domain: 'latticehq.com', resolve: constant('lattice') },
	{ domain: 'lattice.com', resolve: constant('lattice') },
	{ domain: 'cultureamp.com', resolve: constant('culture amp') },
	{ domain: 'rippling.com', resolve: constant('rippling') },
	{ domain: 'gusto.com', resolve: constant('gusto') },
	{ domain: 'deel.com', resolve: constant('deel') },

	// Files
	{ domain: 'box.com', resolve: constant('box') },
	{ domain: 'box.net', resolve: constant('box') },
	{ domain: 'boxcloud.com', resolve: constant('box') },
	{ domain: 'dropbox.com', resolve: constant('dropbox') },
	{ domain: 'db.tt', resolve: constant('dropbox') },
	{ domain: 'wetransfer.com', resolve: constant('wetransfer') },
	{ domain: 'we.tl', resolve: constant('wetransfer') },
	{ domain: 'egnyte.com', resolve: constant('egnyte') },
	{ domain: 'sharefile.com', resolve: constant('sharefile') },
	{ domain: 'sharefile.eu', resolve: constant('sharefile') },

	// AI assistants
	{ domain: 'chatgpt.com', resolve: constant('chatgpt') },
	{ domain: 'chat.openai.com', resolve: constant('chatgpt') },
	{ domain: 'openai.com', resolve: constant('openai') },
	{ domain: 'claude.ai', resolve: constant('claude') },
	{ domain: 'claude.com', resolve: constant('claude') },
	{ domain: 'perplexity.ai', resolve: constant('perplexity') },
	{ domain: 'deepl.com', resolve: constant('deepl') },

	// Test management, CI and release tooling
	{ domain: 'testrail.io', resolve: constant('testrail') },
	{ domain: 'testrail.net', resolve: constant('testrail') },
	{ domain: 'testrail.com', resolve: constant('testrail') },
	{ domain: 'qase.io', resolve: constant('qase') },
	{ domain: 'qtestnet.com', resolve: constant('qtest') },
	{ domain: 'testmo.net', resolve: constant('testmo') },
	{ domain: 'browserstack.com', resolve: constant('browserstack') },
	{ domain: 'testfairy.com', resolve: constant('testfairy') },
	{ domain: 'bitrise.io', resolve: constant('bitrise') },
	{ domain: 'harness.io', resolve: constant('harness') },
	{ domain: 'terraform.io', resolve: constant('terraform') },
	{ domain: 'jamacloud.com', resolve: constant('jama connect') },
	{ domain: 'rallydev.com', resolve: constant('rally') },
	{ domain: 'jam.dev', resolve: constant('jam') },
	{ domain: 'birdeatsbug.com', resolve: constant('bird eats bug') },
	{ domain: 'litmus.com', resolve: constant('litmus') },

	// Hosting and content platforms
	{ domain: 'pantheonsite.io', resolve: constant('pantheon') },
	{ domain: 'wpengine.com', resolve: constant('wp engine') },
	{ domain: 'wpenginepowered.com', resolve: constant('wp engine') },
	{ domain: 'acquia-sites.com', resolve: constant('acquia') },
	{ domain: 'adobeaemcloud.com', resolve: constant('adobe experience manager') },
	{ domain: 'adobecqms.net', resolve: constant('adobe experience manager') },
	{ domain: 'acrobat.adobe.com', resolve: constant('adobe acrobat') },
	{ domain: 'contentful.com', resolve: constant('contentful') },
	{ domain: 'shopify.com', resolve: constant('shopify') },
	{ domain: 'myshopify.com', resolve: constant('shopify') },
	{ domain: 'myvtex.com', resolve: constant('vtex') },
	{ domain: 'readthedocs.io', resolve: constant('read the docs') },
	{ domain: 'digitalocean.com', resolve: constant('digitalocean') },
	{ domain: 'godaddy.com', resolve: constant('godaddy') },
	{ domain: 'draw.io', resolve: constant('draw.io') },
	{ domain: 'diagrams.net', resolve: constant('draw.io') },

	// Data, analytics and governance
	{ domain: 'sigmacomputing.com', resolve: constant('sigma') },
	{ domain: 'mode.com', resolve: constant('mode') },
	{ domain: 'domo.com', resolve: constant('domo') },
	{ domain: 'quickbase.com', resolve: constant('quickbase') },
	{ domain: 'palantirfoundry.com', resolve: constant('palantir foundry') },
	{ domain: 'leanix.net', resolve: constant('leanix') },
	{ domain: 'collibra.com', resolve: constant('collibra') },
	{ domain: 'appiancloud.com', resolve: constant('appian') },
	{ domain: 'cloudera.com', resolve: constant('cloudera') },
	{ domain: 'quantummetric.com', resolve: constant('quantum metric') },
	{ domain: 'groundcover.com', resolve: constant('groundcover') },
	{ domain: 'wiz.io', resolve: constant('wiz') },

	// Security operations
	{ domain: 'crowdstrike.com', resolve: constant('crowdstrike') },
	{ domain: 'sentinelone.net', resolve: constant('sentinelone') },
	{ domain: 'chronicle.security', resolve: constant('google security operations') },
	{ domain: 'virustotal.com', resolve: constant('virustotal') },
	{ domain: 'abuseipdb.com', resolve: constant('abuseipdb') },
	{ domain: 'identitynow.com', resolve: constant('sailpoint') },
	{ domain: 'onetimesecret.com', resolve: constant('onetime secret') },
	{ domain: 'pwpush.com', resolve: constant('password pusher') },

	// Enterprise business systems
	{ domain: 'sap.com', resolve: constant('sap') },
	{ domain: 'oraclecloud.com', resolve: constant('oracle cloud') },
	{ domain: 'oracle.com', resolve: constant('oracle') },
	{ domain: 'coupahost.com', resolve: constant('coupa') },
	{ domain: 'onbmc.com', resolve: constant('bmc helix') },
	{ domain: 'guidewire.net', resolve: constant('guidewire') },
	{ domain: 'inforcloudsuite.com', resolve: constant('infor') },
	{ domain: 'infor.com', resolve: constant('infor') },
	{ domain: 'veevavault.com', resolve: constant('veeva vault') },
	{ domain: 'pure.cloud', resolve: constant('genesys cloud') },
	{ domain: 'five9.com', resolve: constant('five9') },
	{ domain: 'adp.com', resolve: constant('adp') },
	{ domain: 'beeline.com', resolve: constant('beeline') },
	{ domain: 'instructure.com', resolve: constant('canvas') },
	{ domain: 'seismic.com', resolve: constant('seismic') },
	{ domain: 'cybozu.com', resolve: constant('kintone') },
	{ domain: 'backlog.jp', resolve: constant('backlog') },
	{ domain: 'backlog.com', resolve: constant('backlog') },
	{ domain: 'dooray.com', resolve: constant('dooray') },
	{ domain: 'larksuite.com', resolve: constant('lark') },
	{ domain: 'feishu.cn', resolve: constant('lark') },
	{ domain: 'superhuman.com', resolve: constant('superhuman') },
	{ domain: 'access.redhat.com', resolve: constant('red hat') },

	// Payments and customer engagement
	{ domain: 'braintreegateway.com', resolve: constant('braintree') },
	{ domain: 'solidgate.com', resolve: constant('solidgate') },
	{ domain: 'chargebee.com', resolve: constant('chargebee') },
	{ domain: 'billingplatform.com', resolve: constant('billingplatform') },
	{ domain: 'twilio.com', resolve: constant('twilio') },
	{ domain: 'braze.com', resolve: constant('braze') },
	{ domain: 'infobip.com', resolve: constant('infobip') },
	{ domain: 'useinsider.com', resolve: constant('insider') },

	// Screenshots and recordings
	{ domain: 'fathom.video', resolve: constant('fathom') },
	{ domain: 'prnt.sc', resolve: constant('lightshot') },
	{ domain: 'gyazo.com', resolve: constant('gyazo') },
	{ domain: 'zight.com', resolve: constant('zight') },
	{ domain: 'monosnap.ai', resolve: constant('monosnap') },
	{ domain: 'monosnap.com', resolve: constant('monosnap') },
	{ domain: 'awesomescreenshot.com', resolve: constant('awesome screenshot') },
	{ domain: 'screencast.com', resolve: constant('screencast') },
	{ domain: 'screenrec.com', resolve: constant('screenrec') },
	{ domain: 'f.io', resolve: constant('frame.io') },
	{ domain: 'frame.io', resolve: constant('frame.io') },

	// Social, messaging and publishing
	{ domain: 'facebook.com', resolve: constant('facebook') },
	{ domain: 'fb.me', resolve: constant('facebook') },
	{ domain: 'instagram.com', resolve: constant('instagram') },
	{ domain: 'tiktok.com', resolve: constant('tiktok') },
	{ domain: 'reddit.com', resolve: constant('reddit') },
	{ domain: 'medium.com', resolve: constant('medium') },
	{ domain: 't.me', resolve: constant('telegram') },
	{ domain: 'telegram.org', resolve: constant('telegram') },
	{ domain: 'wikipedia.org', resolve: constant('wikipedia') },

	// Link wrappers and shorteners that hide their target
	{ domain: 'urldefense.com', resolve: constant('proofpoint') },
	{ domain: 'ppops.net', resolve: constant('proofpoint') },
	{ domain: 'mimecastprotect.com', resolve: constant('mimecast') },
	{ domain: 'bit.ly', resolve: constant('bitly') },
	{ domain: 'vscode-cdn.net', resolve: constant('vs code') },

	// Standards, reference and public bodies
	{ domain: 'w3.org', resolve: constant('w3c') },
	{ domain: 'schema.org', resolve: constant('schema.org') },
	{ domain: 'mitre.org', resolve: constant('mitre') },
	{ domain: 'cve.org', resolve: constant('cve') },
	{ domain: 'nist.gov', resolve: constant('nist') },
	{ domain: 'nih.gov', resolve: constant('nih') },
	{ domain: 'arxiv.org', resolve: constant('arxiv') },
	{ domain: 'doi.org', resolve: constant('doi') },
	{ domain: 'developer.mozilla.org', resolve: constant('mdn') },
	{ domain: 'kubernetes.io', resolve: constant('kubernetes') },
	{ domain: 'gov.uk', resolve: constant('gov.uk') },
	{ domain: 'nsw.gov.au', resolve: constant('nsw government') },
	{ domain: 'europa.eu', resolve: constant('european union') },
	{ domain: 'overheid.nl', resolve: constant('dutch government') },
	{ domain: 'finra.org', resolve: constant('finra') },
	{ domain: 'acord.org', resolve: constant('acord') },
	{ domain: 'gartner.com', resolve: constant('gartner') },
	{ domain: 'bbc.co.uk', resolve: constant('bbc') },
	{ domain: 'indeed.com', resolve: constant('indeed') },
	{ domain: 'tronscan.org', resolve: constant('tronscan') },
	{ domain: 'netcraft.com', resolve: constant('netcraft') },
	{ domain: 'talosintelligence.com', resolve: constant('cisco talos') },
	{ domain: 'checkpoint.com', resolve: constant('check point') },

	// Organisations whose own sites are often linked (Nov 2026 paste data)
	{ domain: 'adobe.com', resolve: constant('adobe') },
	{ domain: 'apple.com', resolve: constant('apple') },
	{ domain: 'ibm.com', resolve: constant('ibm') },
	{ domain: 'dell.com', resolve: constant('dell') },
	{ domain: 'cisco.com', resolve: constant('cisco') },
	{ domain: 'nvidia.com', resolve: constant('nvidia') },
	{ domain: 'amd.com', resolve: constant('amd') },
	{ domain: 'arm.com', resolve: constant('arm') },
	{ domain: 'qualcomm.com', resolve: constant('qualcomm') },
	{ domain: 'micron.com', resolve: constant('micron') },
	{ domain: 'sandisk.com', resolve: constant('sandisk') },
	{ domain: 'samsung.com', resolve: constant('samsung') },
	{ domain: 'lg.com', resolve: constant('lg') },
	{ domain: 'logitech.com', resolve: constant('logitech') },
	{ domain: 'netgear.com', resolve: constant('netgear') },
	{ domain: 'tp-link.com', resolve: constant('tp-link') },
	{ domain: 'juniper.net', resolve: constant('juniper') },
	{ domain: 'netapp.com', resolve: constant('netapp') },
	{ domain: 'nokia.com', resolve: constant('nokia') },
	{ domain: 'siemens.com', resolve: constant('siemens') },
	{ domain: 'honeywell.com', resolve: constant('honeywell') },
	{ domain: 'liebherr.com', resolve: constant('liebherr') },
	{ domain: 'ptc.com', resolve: constant('ptc') },
	{ domain: 'autodesk.com', resolve: constant('autodesk') },
	{ domain: 'mathworks.com', resolve: constant('mathworks') },
	{ domain: 'trendmicro.com', resolve: constant('trend micro') },
	{ domain: 'veeam.com', resolve: constant('veeam') },
	{ domain: 'verifone.com', resolve: constant('verifone') },
	{ domain: 'here.com', resolve: constant('here') },
	{ domain: 'roku.com', resolve: constant('roku') },
	{ domain: 'nintendo.co.jp', resolve: constant('nintendo') },
	{ domain: 'disney.com', resolve: constant('disney') },
	{ domain: 'pearson.com', resolve: constant('pearson') },
	{ domain: 'wolterskluwer.com', resolve: constant('wolters kluwer') },
	{ domain: 'tylertech.com', resolve: constant('tyler technologies') },
	{ domain: 'centricsoftware.com', resolve: constant('centric software') },
	{ domain: 'everymatrix.com', resolve: constant('everymatrix') },
	{ domain: 'smartico.ai', resolve: constant('smartico') },
	{ domain: 'accenture.com', resolve: constant('accenture') },
	{ domain: 'revolut.com', resolve: constant('revolut') },
	{ domain: 'wise.com', resolve: constant('wise') },
	{ domain: 'zip.com', resolve: constant('zip') },
	{ domain: 'americanexpress.com', resolve: constant('american express') },
	{ domain: 'aexp.com', resolve: constant('american express') },
	{ domain: 'ms.com', resolve: constant('morgan stanley') },
	{ domain: 'usaa.com', resolve: constant('usaa') },
	{ domain: 'vanguard.com', resolve: constant('vanguard') },
	{ domain: 'equifax.com', resolve: constant('equifax') },
	{ domain: 'experian.com', resolve: constant('experian') },
	{ domain: 'corelogic.com', resolve: constant('corelogic') },
	{ domain: 'lpl.com', resolve: constant('lpl financial') },
	{ domain: 'principal.com', resolve: constant('principal') },
	{ domain: 'manulife.com', resolve: constant('manulife') },
	{ domain: 'cigna.com', resolve: constant('cigna') },
	{ domain: 'westpac.com.au', resolve: constant('westpac') },
	{ domain: 'anz.com', resolve: constant('anz') },
	{ domain: 'check24.de', resolve: constant('check24') },
	{ domain: 'alight.com', resolve: constant('alight') },
	{ domain: 'mercedes-benz.com', resolve: constant('mercedes-benz') },
	{ domain: 'daimlertruck.com', resolve: constant('daimler truck') },
	{ domain: 'ford.com', resolve: constant('ford') },
	{ domain: 'gm.com', resolve: constant('general motors') },
	{ domain: 'toyota.com', resolve: constant('toyota') },
	{ domain: 'geotab.com', resolve: constant('geotab') },
	{ domain: 'gomotive.com', resolve: constant('motive') },
	{ domain: 'dhl.com', resolve: constant('dhl') },
	{ domain: 'fedex.com', resolve: constant('fedex') },
	{ domain: 'ups.com', resolve: constant('ups') },
	{ domain: 'auspost.com.au', resolve: constant('australia post') },
	{ domain: 'expediagroup.com', resolve: constant('expedia') },
	{ domain: 'expedia.com', resolve: constant('expedia') },
	{ domain: 'booking.com', resolve: constant('booking.com') },
	{ domain: 'marriott.com', resolve: constant('marriott') },
	{ domain: 'amadeus.com', resolve: constant('amadeus') },
	{ domain: 'amadeus.net', resolve: constant('amadeus') },
	{ domain: 'doordash.com', resolve: constant('doordash') },
	{ domain: 'grab.com', resolve: constant('grab') },
	{ domain: 'bolt.eu', resolve: constant('bolt') },
	{ domain: 'coupang.com', resolve: constant('coupang') },
	{ domain: 'musinsa.com', resolve: constant('musinsa') },
	{ domain: 'bestbuy.com', resolve: constant('best buy') },
	{ domain: 'homedepot.com', resolve: constant('home depot') },
	{ domain: 'starbucks.com', resolve: constant('starbucks') },
	{ domain: 'nespresso.com', resolve: constant('nespresso') },
	{ domain: 'arrow.com', resolve: constant('arrow electronics') },
	{ domain: 'eon.com', resolve: constant('e.on') },
	{ domain: 'telstra.com', resolve: constant('telstra') },
	{ domain: 'telstra.com.au', resolve: constant('telstra') },
	{ domain: 't-mobile.com', resolve: constant('t-mobile') },
	{ domain: 'att.com', resolve: constant('at&t') },
	{ domain: 'vodafone.com', resolve: constant('vodafone') },
	{ domain: 'telus.com', resolve: constant('telus') },
	{ domain: 'telenet.be', resolve: constant('telenet') },
	{ domain: 'kt.com', resolve: constant('kt') },
	{ domain: 'kddi.com', resolve: constant('kddi') },
	{ domain: 'docomo.ne.jp', resolve: constant('docomo') },
	{ domain: 'freee.co.jp', resolve: constant('freee') },
];

const matchesDomain = (hostname: string, domain: string): boolean =>
	hostname === domain || hostname.endsWith(`.${domain}`);

const SAFE_LINKS_DOMAIN = 'safelinks.protection.outlook.com';

const parseUrl = (url: string): URL | null => {
	try {
		return new URL(url);
	} catch {
		return null;
	}
};

/**
 * The URL a link actually points at. Outlook Safe Links wrap the real URL in a `url` query
 * parameter, so they are unwrapped one level; a wrapper inside a wrapper is left unidentified.
 */
const parseLinkTarget = (url: string): URL | null => {
	const parsed = parseUrl(url);
	if (!parsed || !matchesDomain(parsed.hostname.toLowerCase(), SAFE_LINKS_DOMAIN)) {
		return parsed;
	}
	const wrapped = parsed.searchParams.get('url');
	const target = wrapped ? parseUrl(wrapped) : null;
	return target && !matchesDomain(target.hostname.toLowerCase(), SAFE_LINKS_DOMAIN) ? target : null;
};

/** The app a URL belongs to by its host and path, or `null` when the host is not a known app. */
export const getUrlAppId = (url: string | null | undefined): string | null => {
	const parsed = url ? parseLinkTarget(url) : null;
	if (!parsed) {
		return null;
	}
	const hostname = parsed.hostname.toLowerCase();
	const rule = URL_APP_RULES.find(({ domain }) => matchesDomain(hostname, domain));
	return rule ? rule.resolve(parsed) : null;
};
